import React, { useState, useEffect, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import Select from 'react-select';
import { Filter, ChevronDown, Calendar, User, CreditCard, Globe, CheckCircle, XCircle, AlertTriangle, Store, Users } from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { getPartnersList } from '../../services/merchant';
import {
  PreApprovalLog,
  PreApprovalLogFilters,
  fetchPreApprovalLogDetails,
} from '../../services/preApprovalLogs';
import PreApprovalLogDetailModal from './PreApprovalLogDetailModal';
import { UserRole } from '../../types/auth';
import {
  fetchPreApprovalsThunk,
  setPage,
  setPageSize,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setFilters,
  resetFilters
} from '../../features/preApprovalLogs/preApprovalSlice';
import { validateCustomerSearchInput } from "../../utils/searchValidator";

interface SelectOption {
  value: string;
  label: string;
}

const ManagePreApprovalLogs: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector(state => state.auth);
  const {
    logs,
    loading,
    page,
    pageSize,
    total,
    sortBy,
    sortOrder,
    searchQuery,
    filters,
    hasFetched,
  } = useAppSelector(state => state.preApproval);

  const [selectedLog, setSelectedLog] = useState<PreApprovalLog | null>(null);
  const [logDetailOpen, setLogDetailOpen] = useState(false);
  const [logDetailData, setLogDetailData] = useState<any>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<SelectOption[]>([]);
  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true); // allow reset

      // 🔁 Reset table immediately
      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // empty search
      return;
    }

    // Normal validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);
  };


  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };

  const hasSearchOrFilters =
    searchQuery.trim() !== '' ||
    !!filters.platform ||
    !!filters.lender ||
    !!filters.dateRange?.from ||
    !!filters.dateRange?.to ||
    !!filters.partner;

  const isCleverUser = user?.role === UserRole.CLEVERPE_USER;

  const prevRef = useRef({
    searchQuery,
    filters,
    page,
    pageSize,
    sortBy,
    sortOrder,
  });

  // Fetch partners/groups for filter
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const categories = Array.isArray(user?.groups) ? user.groups : [];

        // 🔥 Backend filters partners by category
        const partners = await getPartnersList(categories);

        setAvailableGroups(partners);

        setBrandOptions(
          partners.map(p => ({
            value: p,
            label: p,
          }))
        );
      } catch (error) {
        console.error('Error fetching partner groups:', error);
      }
    };

    fetchGroups();
  }, [user]);



  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder ||
      prev.filters.partner !== filters.partner ||
      prev.filters.lender !== filters.lender ||
      prev.filters.platform !== filters.platform ||
      prev.filters.dateRange?.from !== filters.dateRange?.from ||
      prev.filters.dateRange?.to !== filters.dateRange?.to;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchPreApprovalsThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        filters: {
          ...filters,
          dateRange: { ...filters.dateRange },
        },
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    filters.platform,
    filters.lender,
    filters.partner,
    filters.dateRange?.from,
    filters.dateRange?.to,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);

  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchPreApprovalsThunk());

  }, [token, hasFetched, dispatch]);


  const handleRefresh = () => {
    dispatch(setPage(1));
    dispatch(fetchPreApprovalsThunk());
  };


  const handleRowClick = async (log: PreApprovalLog) => {
    setSelectedLog(log);
    setLogDetailOpen(true);
    setIsLoadingDetail(true);
    try {
      const response = await fetchPreApprovalLogDetails(log._id, token);
      setLogDetailData(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleCloseLogDetail = () => {
    setLogDetailOpen(false);
    setSelectedLog(null);
    setLogDetailData(null);
  };

  const handleFilterChange = (key: keyof PreApprovalLogFilters, value: any) => {
    dispatch(setFilters({ ...filters, [key]: value }));
    dispatch(setPage(1));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const toggleFilters = () => setShowFilters(prev => !prev);

  // Format date
  const formatDate = (dateString: string) => {
    try {
      // Check if the date is already in DD-MM-YYYY HH:MM:SS AM/PM format
      if (/^\d{2}-\d{2}-\d{4}\s+\d{1,2}:\d{2}:\d{2}\s+[AP]M$/.test(dateString)) {
        return dateString;
      }

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString; // Return the original string if parsing fails
    }
  };

  // Format platform for display
  const formatPlatform = (platform: string | null) => {
    if (!platform) return "N/A";

    switch (platform.toLowerCase()) {
      case 'merchant':
        return 'Merchant';
      case 'gateway-partner':
      case 'gateway':
        return 'Gateway';
      case 'customer':
        return 'Website';
      default:
        return platform;
    }
  };

  // Render lender badge with appropriate color
  const renderLenderBadge = (lender: string | null) => {
    if (!lender) return <span className="text-xs text-gray-500">N/A</span>;

    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'cashe-ev':
        bgColor = 'bg-green-500';
        textColor = 'text-white';
        break;
      case 'caprion':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      case 'general':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };

  // Render status badge with appropriate color and icon
  const renderStatusBadge = (response: any) => {
    if (!response) return <span className="text-xs text-gray-500">N/A</span>;

    // Handle Zype error response
    if (response.error) {
      return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center bg-red-100 text-red-800`}>
          <XCircle size={14} className="mr-1" /> {response.error.includes(':') ? response.error.split(':')[1].trim() : response.error}
        </span>
      );
    }

    // Regular status handling
    let icon = null;
    let bgColor = '';
    let textColor = '';
    let displayStatus = response.status || 'N/A';

    // Normalize Zype specific statuses
    if (displayStatus === 'ACCEPT') {
      displayStatus = 'approved';
    } else if (displayStatus === 'PRE_APPROVAL_OFFER_ALREADY_GENERATED') {
      displayStatus = 'approved';
    } else if (displayStatus === 'REJECT') {
      displayStatus = 'rejected';
    }

    switch (displayStatus.toLowerCase()) {
      case 'approved':
        icon = <CheckCircle size={14} className="mr-1" />;
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'rejected':
        icon = <XCircle size={14} className="mr-1" />;
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'pending':
        icon = <AlertTriangle size={14} className="mr-1" />;
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      default:
        icon = <AlertTriangle size={14} className="mr-1" />;
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center ${bgColor} ${textColor}`}>
        {icon} {displayStatus}
      </span>
    );
  };

  // Table columns
  const baseColumns: ColumnDef<PreApprovalLog>[] = [
    {
      id: 'serialNo',
      header: 'Sr. No',
      cell: ({ row }) => {
        const serialNo =
          row.original.serialNumber || (page - 1) * pageSize + row.index + 1;
        return <span className="text-sm">{serialNo}</span>;
      },
      size: 60,
    },
    {
      id: 'customerDetails',
      header: 'Customer Details',
      cell: ({ row }) => {
        const { customerName, customerMobileNo } = row.original;
        if (!customerName && !customerMobileNo) return null;

        return (
          <div className="py-0.5">
            {customerName && (
              <div className="flex items-center text-sm font-medium">
                <User size={12} className="mr-1 text-gray-500" />
                <span
                  className="truncate max-w-[140px]"
                  title={customerName}
                >
                  {customerName}
                </span>
              </div>
            )}
            {customerMobileNo && (
              <div className="text-xs text-gray-500">{customerMobileNo}</div>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      id: 'response',
      header: 'Response',
      cell: ({ row }) => {
        const { preApprovalResponse, lender } = row.original;

        if (lender?.toLowerCase() === 'caprion') {
          const { applicationId, approvedLoanAmount } = preApprovalResponse || {};
          const amount = Number(approvedLoanAmount) || 0;

          return (
            <div className="flex flex-col py-0.5 space-y-0.5">
              <div className="flex items-center space-x-2 text-sm">
                {renderStatusBadge(preApprovalResponse)}
                {amount > 0 && (
                  <div className="font-medium text-gray-900">
                    ₹{amount.toLocaleString('en-IN')}
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-500">
                Application ID: {applicationId ?? 'N/A'}
              </div>
            </div>
          );
        }

        if (lender === 'zype' && preApprovalResponse?.error) {
          return (
            <div className="flex flex-col py-0.5">
              <div className="flex items-center text-sm font-medium text-red-800 bg-red-100 px-2 py-0.5 rounded-full w-fit">
                <XCircle size={12} className="mr-1" /> Rejected
              </div>
              <div className="text-xs text-gray-500 truncate max-w-[200px]">
                {preApprovalResponse.error.includes(':')
                  ? preApprovalResponse.error.split(':')[1].trim()
                  : preApprovalResponse.error.replace(
                    'Pre-approval failed: ',
                    ''
                  )}
              </div>
            </div>
          );
        }

        return (
          <div className="flex items-center space-x-2 text-sm">
            <div>{renderStatusBadge(preApprovalResponse)}</div>
            {preApprovalResponse &&
              !preApprovalResponse.error &&
              typeof preApprovalResponse.amount === 'number' &&
              preApprovalResponse.amount > 0 && (
                <div className="font-medium text-gray-900">
                  ₹{preApprovalResponse.amount.toLocaleString('en-IN')}
                </div>
              )}
          </div>
        );
      },
      size: 200,
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <div className="flex items-start text-sm py-0.5">
          <Calendar size={12} className="text-gray-500 mr-1 mt-0.5" />
          <span>{formatDate(row.original.createdAt)}</span>
        </div>
      ),
      size: 160,
    },
  ];

  // 🔁 Conditionally insert Lender / Platform / Merchant / Partner
  if (user?.role !== UserRole.CLEVERPE_USER) {
    baseColumns.splice(
      2,
      0,
      {
        id: 'lender',
        header: 'Lender',
        accessorKey: 'lender',
        cell: ({ row }) => {
          const { lender } = row.original;
          if (!lender) return null;
          return (
            <div className="flex items-center text-sm">
              {renderLenderBadge(lender)}
            </div>
          );
        },
        size: 110,
      },
      {
        id: 'platform',
        header: 'Platform',
        accessorKey: 'platform',
        cell: ({ row }) => {
          const platform = row.original.platform;
          // if (!platform || platform === 'website' || platform === 'customer') {
          //   return null;
          // }
          return (
            <div className="flex items-center text-sm">
              <Globe size={12} className="mr-1 text-green-500" />
              <span>{formatPlatform(platform)}</span>
            </div>
          );
        },
        size: 120,
      },
      // {
      //   id: 'merchantDetails',
      //   header: 'Merchant Details',
      //   cell: ({ row }) => {
      //     const { merchantName, merchantMobileNo } = row.original;
      //     if (!merchantName && !merchantMobileNo) return null;

      //     return (
      //       <div className="py-0.5">
      //         {merchantName && (
      //           <div className="flex items-center text-sm font-medium">
      //             <Store size={12} className="mr-1 text-gray-500" />
      //             <span
      //               className="truncate max-w-[140px]"
      //               title={merchantName}
      //             >
      //               {merchantName}
      //             </span>
      //           </div>
      //         )}
      //         {merchantMobileNo && (
      //           <div className="text-xs text-gray-500">{merchantMobileNo}</div>
      //         )}
      //       </div>
      //     );
      //   },
      //   size: 200,
      // },
      // {
      //   id: 'partner',
      //   header: 'Partner',
      //   accessorKey: 'partner',
      //   cell: ({ row }) => {
      //     const partnerName = row.original.partnerName;
      //     if (!partnerName) return null;

      //     return (
      //       <div className="flex items-center text-sm font-medium">
      //         <Users size={12} className="mr-1 text-indigo-500" />
      //         <span className="truncate max-w-[140px]">{partnerName}</span>
      //       </div>
      //     );
      //   },
      //   size: 140,
      // }
    );
  }





  const columns: ColumnDef<PreApprovalLog>[] = baseColumns;

  console.log("User Role:", user?.role);
  console.log("Full Redux user object:", user);
  console.log("User Lender:", user?.lender, "Is Array:", Array.isArray(user?.lender));

  return (
    <div className="w-full no-scrollbar">
      {/* Filters and search section */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="outline"
          onClick={toggleFilters}
          className="flex items-center"
          size="sm"
        >
          <Filter size={14} className="mr-1" />
          Filters
          <ChevronDown size={14} className="ml-1" />
        </Button>
      </div>

      {/* Filter controls */}
      {showFilters && (
        <div className="p-4 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm no-scrollbar text-sm">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Platform filter */}
            {user?.role !== UserRole.CLEVERPE_USER && (
              <div className="flex flex-col w-52">
                <label className="mb-1 font-medium text-gray-700 text-sm">Platform</label>
                <select
                  value={filters.platform}
                  onChange={(e) => handleFilterChange('platform', e.target.value)}
                  className="w-full h-9 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Platforms</option>
                  <option value="gateway-partner">Gateway</option>
                  <option value="merchant">Merchant</option>
                  <option value="customer">Website</option>
                </select>
              </div>
            )}

            {user?.role === UserRole.CLEVERPE_USER && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Partner/Brand</label>
                <Select
                  options={brandOptions}
                  value={brandOptions.find(
                    option => option.value === filters.partner
                  )}
                  onChange={(option) =>
                    dispatch(
                      setFilters({
                        ...filters,
                        partner: option?.value || '',
                      })
                    )
                  }
                  isClearable
                  placeholder="Select Brand"
                />

              </div>
            )}

            {/* Partner/Group filter */}
            {user?.role !== UserRole.CLEVERPE_USER && (
              <div className="flex flex-col w-60">
                <label className="mb-1 font-medium text-gray-700 text-sm">Partner/Group</label>
                <Select
                  value={filters.partner ? { value: filters.partner, label: filters.partner } : null}
                  onChange={(option) => handleFilterChange('partner', option ? option.value : '')}
                  options={availableGroups.map(group => ({ value: group, label: group }))}
                  isClearable
                  placeholder="Search or select partner..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderColor: '#D1D5DB',
                      borderRadius: '0.5rem',
                      minHeight: '36px',      // 👈 reduced
                      height: '36px',         // 👈 force height
                      boxShadow: 'none'
                    }),
                    valueContainer: (baseStyles) => ({
                      ...baseStyles,
                      padding: '0 10px',       // 👈 reduce vertical padding
                      height: '34px'
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                      margin: '0px',
                      padding: '0px'
                    }),
                    indicatorsContainer: (baseStyles) => ({
                      ...baseStyles,
                      height: '34px'
                    }),
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: '#9CA3AF',
                      margin: '0'
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isSelected
                        ? '#E0E7FF'
                        : state.isFocused
                          ? '#F3F4F6'
                          : 'transparent',
                      color: state.isSelected ? '#4F46E5' : '#374151',
                      ':active': {
                        backgroundColor: '#E0E7FF'
                      }
                    })
                  }}
                />

              </div>
            )}

            {/* Lender filter */}
            {user?.role !== UserRole.CLEVERPE_USER && (
              <div className="flex flex-col w-52">
                <label className="mb-1 font-medium text-gray-700 text-sm">Lender</label>
                <select
                  value={filters.lender}
                  onChange={(e) => handleFilterChange('lender', e.target.value)}
                  className="w-full h-9 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Lenders</option>
                  {['cashe', 'cashe-ev', 'zype'].map((l) => (
                    <option key={l} value={l}>
                      {l === 'cashe'
                        ? 'Cashe'
                        : l === 'cashe-ev'
                          ? 'Cashe-Ev'
                          : l === 'zype'
                            ? 'Zype'
                            : l}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date range filters */}
            <div className="flex gap-2 items-end">
              <div className="flex flex-col w-52">
                <label className="mb-1 font-medium text-gray-700 text-sm">From</label>
                <input
                  type="date"
                  value={filters.dateRange?.from || ''}
                  onChange={(e) =>
                    handleFilterChange('dateRange', { from: e.target.value || undefined })
                  }
                  className="w-full h-9 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col w-52">
                <label className="mb-1 font-medium text-gray-700 text-sm">To</label>
                <input
                  type="date"
                  value={filters.dateRange?.to || ''}
                  onChange={(e) =>
                    handleFilterChange('dateRange', { to: e.target.value || undefined })
                  }
                  className="w-full h-9 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Filter actions on next line */}
          <div className="flex justify-end mt-3 gap-2">
            <Button variant="outline" onClick={handleResetFilters} size="sm" className="px-3 py-1 text-sm">
              Reset
            </Button>
            <Button onClick={() => {
              dispatch(fetchPreApprovalsThunk());
            }}
              size="sm" className="px-3 py-1 text-sm">
              Apply
            </Button>
          </div>
        </div>
      )}







      {/* Pre-Approval Logs Table */}
      {/* Always show search bar through CommonTable */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={logs} // ✅ Show real data if allowed
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(newpage) => dispatch(setPage(newpage))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          searchValue={localSearch}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!isSearchValid}
          searchError={searchError}
          onResetSearch={handleResetSearch} // ✅ new

          onRefresh={handleRefresh}
          searchPlaceholder="Search by name, mobile number..."
          enableRowClick={true}
          onRowClick={handleRowClick}
        // emptyStateMessage={
        //   isCleverUser && !hasSearchOrFilters
        //     ? 'Please apply search or filter to view logs.'
        //     : 'No results found.'
        // }
        />
      </div>



      {/* Pre-Approval Log Detail Modal */}
      <PreApprovalLogDetailModal
        isOpen={logDetailOpen}
        onClose={handleCloseLogDetail}
        logData={logDetailData}
        selectedLog={selectedLog}
        isLoading={isLoadingDetail}
      />
    </div>
  );
};

export default ManagePreApprovalLogs; 